/**
 * NetworkMonitor — centralized NetInfo transition detector.
 *
 * Owns a single NetInfo subscription and fans out a **"network regained"**
 * event to subscribers on the rising edge of connectivity. This is the signal
 * the rest of the app needs after an abrupt network transition (Wi-Fi
 * drop/restore, Wi-Fi↔cellular handoff, VPN flap): the OS holds stale, dead
 * TCP connections that must be dropped and rebuilt.
 *
 * Scope: this does NOT replace the two existing NetInfo consumers, which solve
 * different problems and keep their own subscriptions:
 *   - `syncQueue.ts`        — flush the write queue on `isConnected` restore
 *   - `endpoint-errors.ts`  — one-shot `NetInfo.fetch()` to classify a failure
 *
 * This module adds the missing *transition* signal (an edge, not a level) and
 * centralizes it so we don't accrete a third ad-hoc subscription per consumer.
 *
 * Dependency rule: the monitor never imports socket/screens. Consumers
 * subscribe via `onNetworkRegained` — keeps the dependency graph acyclic, the
 * same pattern `socket.ts` uses with `onSocketReady`.
 *
 * See `docs/specs/mobile/network-resilience.mobile.md` (NETRES-001).
 */

import NetInfo from '@react-native-community/netinfo';
import type { NetInfoState, NetInfoSubscription } from '@react-native-community/netinfo';
import { createLogger } from '../utils/logger';
import { E2E_MODE } from '../config/constants';

const log = createLogger('NetworkMonitor');

/** Debounce window: a single physical transition emits a burst of NetInfo
 *  events; collapse them into one regain fan-out. */
const NET_REGAIN_DEBOUNCE_MS = 500;

export type NetworkRegainHandler = () => void;

/**
 * Derive a coarse online/offline view from a NetInfo state.
 *
 * - `isConnected === true` AND `isInternetReachable !== false` → online.
 *   `isInternetReachable` is `null` while unknown (transiently); only an
 *   explicit `false` counts as offline so we never suppress a real regain.
 */
function deriveOnline(state: Pick<NetInfoState, 'isConnected' | 'isInternetReachable'>): boolean {
  return state.isConnected === true && state.isInternetReachable !== false;
}

class NetworkMonitorService {
  private unsubscribeNetInfo: NetInfoSubscription | null = null;
  private handlers = new Set<NetworkRegainHandler>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Last-seen derived online state, for rising-edge detection. */
  private online = false;
  /** Last-seen connection type, to detect interface handoffs while online. */
  private lastType: NetInfoState['type'] | null = null;
  /**
   * False until the first NetInfo callback establishes the baseline.
   * `NetInfo.addEventListener` fires synchronously with the current state on
   * subscribe; treating that as a transition would spuriously fire a regain
   * (and a needless socket reconnect) right after `start()`. The first
   * callback only records state — it never fans out.
   */
  private primed = false;

  /**
   * Begin monitoring. Idempotent — a second call while already started is a
   * no-op (mirrors `syncQueue.initialize`'s guard).
   */
  start(): void {
    if (this.unsubscribeNetInfo) return;
    this.online = false;
    this.lastType = null;
    this.primed = false;
    this.unsubscribeNetInfo = NetInfo.addEventListener(this.handleStateChange);
    log.debug('NetworkMonitor started');
  }

  /** Stop monitoring: remove the NetInfo subscription and drop all handlers. */
  stop(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.unsubscribeNetInfo?.();
    this.unsubscribeNetInfo = null;
    this.handlers.clear();
    this.online = false;
    this.lastType = null;
    this.primed = false;
    log.debug('NetworkMonitor stopped');
  }

  /**
   * Subscribe to network-regain edges. Returns an unsubscribe function.
   * Safe to call before `start()` — handlers persist until `stop()`.
   */
  onNetworkRegained(handler: NetworkRegainHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** Current derived online view (test/diagnostic seam). */
  isOnline(): boolean {
    return this.online;
  }

  /**
   * E2E seam: push a synthetic NetInfo state through the real transition logic.
   *
   * Verifying the regain edge otherwise requires physically toggling the host's
   * Wi-Fi — unavailable to an agent, unreliable in CI, and the reason NETRES-001
   * was the one behaviour in `network-resilience.mobile.md` that device testing
   * never closed. This drives `handleStateChange` exactly as the real NetInfo
   * subscription does, so debouncing, priming, rising-edge detection and
   * interface-handoff detection all run for real — the *source* of the state is
   * the only thing faked.
   *
   * It does NOT cut real sockets, and the two other NetInfo consumers
   * (`syncQueue`, `endpoint-errors`) own separate subscriptions and never see
   * this. It exercises the app's reaction to a transition, not an OS outage.
   *
   * **Inert outside E2E.** Returns false and does nothing unless `E2E_MODE` is
   * on, so this cannot be used to spoof connectivity in a production build. The
   * only caller is the `__DEV__`-stripped `dev.set-network-state` handler.
   *
   * @returns whether the state was applied (false ⇒ not in E2E_MODE).
   */
  applyFakeNetworkState(state: {
    isConnected: boolean;
    isInternetReachable: boolean | null;
    type: string;
  }): boolean {
    if (!E2E_MODE) return false;
    log.info('Applying faked network state (E2E)', state);
    this.handleStateChange(state as unknown as NetInfoState);
    return true;
  }

  // ── Internal ───────────────────────────────────────────────────────

  private handleStateChange = (state: NetInfoState): void => {
    const nowOnline = deriveOnline(state);

    // First callback establishes the baseline without firing (see `primed`).
    if (!this.primed) {
      this.primed = true;
      this.online = nowOnline;
      this.lastType = state.type;
      return;
    }

    const wasOnline = this.online;
    const prevType = this.lastType;

    this.online = nowOnline;
    this.lastType = state.type;

    if (!nowOnline) return; // offline or still-offline — nothing to fan out

    // Rising edge: offline → online.
    const regainedConnectivity = !wasOnline;
    // Interface handoff while online (e.g. wifi → cellular): pooled
    // connections from the old interface are dead even though `isConnected`
    // never dipped to false.
    const changedInterface = wasOnline && prevType !== null && prevType !== state.type;

    if (regainedConnectivity || changedInterface) {
      log.info('Network regained', {
        reason: regainedConnectivity ? 'reconnect' : 'interface-change',
        type: state.type,
        prevType,
      });
      this.scheduleFanOut();
    }
  };

  private scheduleFanOut(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.fanOut();
    }, NET_REGAIN_DEBOUNCE_MS);
  }

  private fanOut(): void {
    // Snapshot so a handler that (un)subscribes during fan-out doesn't mutate
    // the set mid-iteration.
    for (const handler of Array.from(this.handlers)) {
      try {
        handler();
      } catch (err) {
        log.warn('Network-regain handler threw', err);
      }
    }
  }
}

export const networkMonitor = new NetworkMonitorService();
